<template>
  <div class="recipients-page">
    <div class="page-header">
      <div>
        <h2>WhatsApp Broadcast Recipients</h2>
        <p>View and filter recipients across all campaigns.</p>
      </div>

      <button
        type="button"
        class="btn btn-secondary"
        :disabled="loading"
        @click="fetchRecipients"
      >
        Refresh
      </button>

      <div class="header-actions">
      <button
        type="button"
        class="btn btn-export"
        :disabled="loading || exporting"
        @click="exportRecipients"
      >
        {{ exporting ? "Exporting..." : "Export CSV" }}
      </button>

      <button
        type="button"
        class="btn btn-secondary"
        :disabled="loading || exporting"
        @click="fetchRecipients"
      >
        Refresh
      </button>
    </div>

    </div>

    <div class="filters-card">
      <div class="filters-grid">
        <div class="form-group">
          <label>Campaign</label>

          <select v-model="filters.campaign_id" @change="applyFilters">
            <option value="">All Campaigns</option>

            <option
              v-for="campaign in campaigns"
              :key="campaign.id"
              :value="campaign.id"
            >
              {{ campaign.name }}
            </option>
          </select>
        </div>

        <div class="form-group">
          <label>Status</label>

          <select v-model="filters.status" @change="applyFilters">
            <option value="">All Statuses</option>
            <option value="PENDING">Pending</option>
            <option value="PROCESSING">Processing</option>
            <option value="SENT">Sent</option>
            <option value="DELIVERED">Delivered</option>
            <option value="READ">Read</option>
            <option value="FAILED">Failed</option>
          </select>
        </div>

        <div class="form-group">
          <label>Phone</label>

          <input
            v-model.trim="filters.phone"
            type="text"
            placeholder="Search phone"
            @keyup.enter="applyFilters"
          />
        </div>

        <div class="form-group">
          <label>Name</label>

          <input
            v-model.trim="filters.name"
            type="text"
            placeholder="Search recipient"
            @keyup.enter="applyFilters"
          />
        </div>

        <div class="form-group">
          <label>From Date</label>

          <input
            v-model="filters.from"
            type="date"
            @change="applyFilters"
          />
        </div>

        <div class="form-group">
          <label>To Date</label>

          <input
            v-model="filters.to"
            type="date"
            @change="applyFilters"
          />
        </div>
      </div>

      <div class="filter-actions">
        <button
          type="button"
          class="btn btn-primary"
          :disabled="loading"
          @click="applyFilters"
        >
          Search
        </button>

        <button
          type="button"
          class="btn btn-secondary"
          :disabled="loading"
          @click="clearFilters"
        >
          Clear Filters
        </button>
      </div>
    </div>

    <div v-if="errorMessage" class="alert alert-error">
      {{ errorMessage }}
    </div>

    <div class="summary-row">
      <div>
        Total recipients:
        <strong>{{ pagination.total }}</strong>
      </div>

      <div class="page-size">
        <label>Rows per page</label>

        <select v-model.number="pagination.limit" @change="changePageSize">
          <option :value="25">25</option>
          <option :value="50">50</option>
          <option :value="100">100</option>
          <option :value="200">200</option>
        </select>
      </div>
    </div>

    <div class="table-card">
      <div v-if="loading" class="loading-state">
        Loading recipients...
      </div>

      <div v-else-if="recipients.length === 0" class="empty-state">
        No recipients found.
      </div>

      <div v-else class="table-wrapper">
        <table>
          <thead>
            <tr>
              <th @click="changeSort('id')">
                ID
                <SortIcon :column="'id'" :sort="sort" />
              </th>

              <th>Campaign</th>

              <th>Name</th>

              <th>Phone</th>

              <th @click="changeSort('status')">
                Status
                <SortIcon :column="'status'" :sort="sort" />
              </th>
              <th @click="changeSort('delivery_status')">
                Delivery Status
                <SortIcon :column="'delivery_status'" :sort="sort" />
              </th>

              <th @click="changeSort('attempts')">
                Attempts
                <SortIcon :column="'attempts'" :sort="sort" />
              </th>

              <th>Error</th>

              <th @click="changeSort('sent_at')">
                Sent At
                <SortIcon :column="'sent_at'" :sort="sort" />
              </th>

              <th @click="changeSort('created_at')">
                Created At
                <SortIcon :column="'created_at'" :sort="sort" />
              </th>
            </tr>
          </thead>

          <tbody>
            <tr v-for="recipient in recipients" :key="recipient.id">
              <td>{{ recipient.id }}</td>

              <td>
                <div class="campaign-cell">
                  <strong>
                    {{ recipient.campaign_name || `Campaign #${recipient.campaign_id}` }}
                  </strong>

                  <span
                    v-if="recipient.campaign_status"
                    class="secondary-text"
                  >
                    {{ recipient.campaign_status }}
                  </span>
                </div>
              </td>

              <td>{{ recipient.name || "-" }}</td>

              <td>{{ recipient.phone || "-" }}</td>

              <td>
                <span
                  class="status-badge"
                  :class="getStatusClass(recipient.status)"
                >
                  {{ recipient.status || "UNKNOWN" }}
                </span>
              </td>
              <td>
                <span
                  class="status-badge"
                  :class="getStatusClass(recipient.delivery_status)"
                >
                  {{ recipient.delivery_status || "UNKNOWN" }}
                </span>
              </td>

              <td>{{ recipient.attempts ?? 0 }}</td>

              <td>
                <span
                  v-if="recipient.error_message"
                  class="error-text"
                  :title="recipient.error_message"
                >
                  {{ truncateText(recipient.error_message, 50) }}
                </span>

                <span v-else>-</span>
              </td>

              <td>{{ formatDateTime(recipient.sent_at) }}</td>

              <td>{{ formatDateTime(recipient.created_at) }}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <div v-if="pagination.totalPages > 1" class="pagination">
      <button
        type="button"
        class="btn btn-secondary"
        :disabled="loading || pagination.page <= 1"
        @click="goToPage(pagination.page - 1)"
      >
        Previous
      </button>

      <button
        v-for="page in visiblePages"
        :key="page"
        type="button"
        class="page-button"
        :class="{ active: page === pagination.page }"
        :disabled="loading"
        @click="goToPage(page)"
      >
        {{ page }}
      </button>

      <button
        type="button"
        class="btn btn-secondary"
        :disabled="
          loading ||
          pagination.page >= pagination.totalPages
        "
        @click="goToPage(pagination.page + 1)"
      >
        Next
      </button>
    </div>
  </div>
</template>

<script setup>
import {
  computed,
  defineComponent,
  h,
  onMounted,
  reactive,
  ref,
} from "vue";
import { auth } from "@/services/auth";
import VARS from "@/services/vars";

// const props = defineProps({
//   apiBaseUrl: {
//     type: String,
//     default: "",
//   },

//   token: {
//     type: String,
//     default: "",
//   },
// });

const recipients = ref([]);
const campaigns = ref([]);
const loading = ref(false);
const errorMessage = ref("");
const exporting = ref(false);


const filters = reactive({
  campaign_id: "",
  status: "",
  delivery_status: "",
  phone: "",
  name: "",
  from: "",
  to: "",
});


const pagination = reactive({
  page: 1,
  limit: 50,
  total: 0,
  totalPages: 0,
});

const sort = reactive({
  column: "created_at",
  order: "DESC",
});

const SortIcon = defineComponent({
  props: {
    column: {
      type: String,
      required: true,
    },

    sort: {
      type: Object,
      required: true,
    },
  },

  setup(componentProps) {
    return () => {
      if (componentProps.sort.column !== componentProps.column) {
        return h("span", { class: "sort-icon inactive" }, "↕");
      }

      return h(
        "span",
        { class: "sort-icon" },
        componentProps.sort.order === "ASC" ? "↑" : "↓"
      );
    };
  },
});

const visiblePages = computed(() => {
  const currentPage = pagination.page;
  const totalPages = pagination.totalPages;
  const pageCount = 5;

  let startPage = Math.max(1, currentPage - Math.floor(pageCount / 2));
  let endPage = Math.min(totalPages, startPage + pageCount - 1);

  if (endPage - startPage + 1 < pageCount) {
    startPage = Math.max(1, endPage - pageCount + 1);
  }

  const pages = [];

  for (let page = startPage; page <= endPage; page += 1) {
    pages.push(page);
  }

  return pages;
});

function buildHeaders() {
  const headers = {
    Accept: "application/json",
    "Content-Type": "application/json",
  };


  if (auth.token) {
    headers.Authorization = `Bearer ${auth.token}`;
  }

  return headers;
}



function buildQueryParams({
  includePagination = true,
} = {}) {
  const params = new URLSearchParams();

  if (filters.campaign_id) {
    params.set(
      "campaign_id",
      String(filters.campaign_id)
    );
  }

  if (filters.status) {
    params.set("status", filters.status);
  }

  if (filters.delivery_status) {
    params.set(
      "delivery_status",
      filters.delivery_status
    );
  }

  if (filters.phone) {
    params.set("phone", filters.phone);
  }

  if (filters.name) {
    params.set("name", filters.name);
  }

  if (filters.from) {
    params.set("from", filters.from);
  }

  if (filters.to) {
    params.set("to", filters.to);
  }

  params.set("sort", sort.column);
  params.set("order", sort.order);

  if (includePagination) {
    params.set("page", String(pagination.page));
    params.set("limit", String(pagination.limit));
  }

  return params.toString();
}




function getDownloadFilename(
  contentDisposition,
  fallbackFilename
) {
  if (!contentDisposition) {
    return fallbackFilename;
  }

  const utf8Match = contentDisposition.match(
    /filename\*=UTF-8''([^;]+)/
  );

  if (utf8Match?.[1]) {
    return decodeURIComponent(utf8Match[1]);
  }

  const normalMatch = contentDisposition.match(
    /filename="?([^";]+)"?/i
  );

  return normalMatch?.[1] || fallbackFilename;
}

async function exportRecipients() {
  exporting.value = true;
  errorMessage.value = "";

  try {
    const query = buildQueryParams({
      includePagination: false,
    });

    const response = await fetch(
      `${VARS.API_URL}/api/manager/wa-broadcast/recipients/export?${query}`,
      {
        method: "GET",
        credentials: "include",
        headers: buildHeaders(),
      }
    );

    if (!response.ok) {
      let message = "Unable to export recipients.";

      try {
        const errorResult = await response.json();

        message =
          errorResult.message ||
          errorResult.error ||
          message;
      } catch {
        // Response may not be JSON
      }

      throw new Error(message);
    }

    const blob = await response.blob();

    const fallbackFilename =
      `whatsapp-broadcast-recipients-${new Date()
        .toISOString()
        .slice(0, 10)}.csv`;

    const filename = getDownloadFilename(
      response.headers.get("content-disposition"),
      fallbackFilename
    );

    const downloadUrl = URL.createObjectURL(blob);
    const link = document.createElement("a");

    link.href = downloadUrl;
    link.download = filename;

    document.body.appendChild(link);
    link.click();
    link.remove();

    URL.revokeObjectURL(downloadUrl);
  } catch (error) {
    console.error("Recipient export error:", error);

    errorMessage.value =
      error.message ||
      "Something went wrong while exporting recipients.";
  } finally {
    exporting.value = false;
  }
}



// function buildQueryParams() {
//   const params = new URLSearchParams();

//   if (filters.campaign_id) {
//     params.set("campaign_id", filters.campaign_id);
//   }

//   if (filters.status) {
//     params.set("status", filters.status);
//   }

//   if (filters.phone) {
//     params.set("phone", filters.phone);
//   }

//   if (filters.name) {
//     params.set("name", filters.name);
//   }

//   if (filters.from) {
//     params.set("from", filters.from);
//   }

//   if (filters.to) {
//     params.set("to", filters.to);
//   }

//   params.set("page", String(pagination.page));
//   params.set("limit", String(pagination.limit));
//   params.set("sort", sort.column);
//   params.set("order", sort.order);

//   return params.toString();
// }

async function fetchRecipients() {
  loading.value = true;
  errorMessage.value = "";

  try {
    const query = buildQueryParams();

    const response = await fetch(
      `${VARS.API_URL}/api/manager/wa-broadcast/recipients?${query}`,
      {
        method: "GET",
        credentials: "include",
        headers: buildHeaders(),
      }
    );

    const result = await response.json();

    if (!response.ok || !result.ok) {
      throw new Error(
        result.message || "Unable to load broadcast recipients."
      );
    }

    recipients.value = Array.isArray(result.data)
      ? result.data
      : [];

    pagination.total = Number(result.total || 0);
    pagination.page = Number(result.page || pagination.page);
    pagination.limit = Number(result.limit || pagination.limit);
    pagination.totalPages = Number(result.totalPages || 0);
  } catch (error) {
    console.error("Recipient fetch error:", error);

    recipients.value = [];
    pagination.total = 0;
    pagination.totalPages = 0;

    errorMessage.value =
      error.message || "Something went wrong while loading recipients.";
  } finally {
    loading.value = false;
  }
}

async function fetchCampaigns() {
  try {
    const response = await fetch(
      `${VARS.API_URL}/api/manager/wa-broadcast/campaigns?limit=1000`,
      {
        method: "GET",
        credentials: "include",
        headers: buildHeaders(),
      }
    );

    const result = await response.json();

    if (!response.ok || !result.ok) {
      return;
    }

    campaigns.value = Array.isArray(result.data)
      ? result.data
      : [];
  } catch (error) {
    console.error("Campaign fetch error:", error);
  }
}

function applyFilters() {
  pagination.page = 1;
  fetchRecipients();
}

function clearFilters() {
  filters.campaign_id = "";
  filters.status = "";
  filters.delivery_status = "";
  filters.phone = "";
  filters.name = "";
  filters.from = "";
  filters.to = "";

  pagination.page = 1;
  sort.column = "created_at";
  sort.order = "DESC";

  fetchRecipients();
}

function goToPage(page) {
  if (
    page < 1 ||
    page > pagination.totalPages ||
    page === pagination.page
  ) {
    return;
  }

  pagination.page = page;
  fetchRecipients();
}

function changePageSize() {
  pagination.page = 1;
  fetchRecipients();
}

function changeSort(column) {
  if (sort.column === column) {
    sort.order = sort.order === "ASC" ? "DESC" : "ASC";
  } else {
    sort.column = column;
    sort.order = "DESC";
  }

  pagination.page = 1;
  fetchRecipients();
}

function getStatusClass(status) {
  const normalizedStatus = String(status || "").toUpperCase();

  const statusClasses = {
    PENDING: "status-pending",
    PROCESSING: "status-processing",
    SENT: "status-sent",
    DELIVERED: "status-delivered",
    READ: "status-read",
    FAILED: "status-failed",
  };

  return statusClasses[normalizedStatus] || "status-default";
}

function formatDateTime(value) {
  if (!value) {
    return "-";
  }

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return value;
  }

  return new Intl.DateTimeFormat("en-IN", {
    dateStyle: "medium",
    timeStyle: "short",
  }).format(date);
}

function truncateText(value, length = 50) {
  if (!value) {
    return "";
  }

  const text = String(value);

  return text.length > length
    ? `${text.slice(0, length)}...`
    : text;
}

onMounted(async () => {
  await Promise.all([
    fetchCampaigns(),
    fetchRecipients(),
  ]);
});
</script>

<style scoped>
.recipients-page {
  padding: 24px;
  background: #f8fafc;
  min-height: 100vh;
  color: #1e293b;
}

.page-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 20px;
}

.page-header h2 {
  margin: 0;
  font-size: 24px;
}

.page-header p {
  margin: 6px 0 0;
  color: #64748b;
}

.filters-card,
.table-card {
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgb(15 23 42 / 5%);
}

.filters-card {
  padding: 20px;
  margin-bottom: 20px;
}

.filters-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(180px, 1fr));
  gap: 16px;
}

.form-group {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.form-group label {
  font-size: 13px;
  font-weight: 600;
  color: #475569;
}

.form-group input,
.form-group select,
.page-size select {
  width: 100%;
  min-height: 40px;
  padding: 8px 10px;
  border: 1px solid #cbd5e1;
  border-radius: 8px;
  background: #ffffff;
  color: #1e293b;
  outline: none;
}

.form-group input:focus,
.form-group select:focus,
.page-size select:focus {
  border-color: #64748b;
}

.filter-actions {
  display: flex;
  gap: 10px;
  margin-top: 18px;
}

.btn {
  min-height: 40px;
  padding: 8px 16px;
  border: 0;
  border-radius: 8px;
  cursor: pointer;
  font-weight: 600;
}

.btn:disabled {
  opacity: 0.55;
  cursor: not-allowed;
}

.btn-primary {
  background: #0f172a;
  color: #ffffff;
}

.btn-secondary {
  background: #e2e8f0;
  color: #1e293b;
}

.alert {
  padding: 12px 16px;
  margin-bottom: 16px;
  border-radius: 8px;
}

.alert-error {
  background: #fee2e2;
  color: #991b1b;
  border: 1px solid #fecaca;
}

.summary-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 12px;
}

.page-size {
  display: flex;
  align-items: center;
  gap: 8px;
}

.page-size select {
  width: auto;
  min-width: 80px;
}

.table-card {
  overflow: hidden;
}

.table-wrapper {
  overflow-x: auto;
}

table {
  width: 100%;
  min-width: 1200px;
  border-collapse: collapse;
}

thead {
  background: #f1f5f9;
}

th,
td {
  padding: 13px 14px;
  border-bottom: 1px solid #e2e8f0;
  text-align: left;
  vertical-align: middle;
  font-size: 14px;
}

th {
  white-space: nowrap;
  font-size: 13px;
  color: #475569;
  user-select: none;
}

th[onclick],
th:hover {
  cursor: pointer;
}

tbody tr:hover {
  background: #f8fafc;
}

.campaign-cell {
  display: flex;
  flex-direction: column;
  gap: 3px;
}

.secondary-text {
  color: #64748b;
  font-size: 12px;
}

.status-badge {
  display: inline-flex;
  align-items: center;
  padding: 4px 9px;
  border-radius: 999px;
  font-size: 11px;
  font-weight: 700;
}

.status-pending {
  background: #fef3c7;
  color: #92400e;
}

.status-processing {
  background: #dbeafe;
  color: #1d4ed8;
}

.status-sent {
  background: #e0e7ff;
  color: #4338ca;
}

.status-delivered {
  background: #dcfce7;
  color: #166534;
}

.status-read {
  background: #ccfbf1;
  color: #115e59;
}

.status-failed {
  background: #fee2e2;
  color: #991b1b;
}

.status-default {
  background: #e2e8f0;
  color: #475569;
}


.header-actions {
  display: flex;
  align-items: center;
  gap: 10px;
}

.btn-export {
  background: #15803d;
  color: #ffffff;
}

.btn-export:hover:not(:disabled) {
  background: #166534;
}

@media (max-width: 640px) {
  .header-actions {
    width: 100%;
    flex-direction: column;
  }

  .header-actions .btn {
    width: 100%;
  }
}


.error-text {
  color: #b91c1c;
  cursor: help;
}

.loading-state,
.empty-state {
  padding: 50px 20px;
  text-align: center;
  color: #64748b;
}

.pagination {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 8px;
  margin-top: 20px;
}

.page-button {
  min-width: 40px;
  height: 40px;
  padding: 0 10px;
  border: 1px solid #cbd5e1;
  border-radius: 8px;
  background: #ffffff;
  cursor: pointer;
}

.page-button.active {
  background: #0f172a;
  border-color: #0f172a;
  color: #ffffff;
}

.page-button:disabled {
  cursor: not-allowed;
  opacity: 0.6;
}

.sort-icon {
  margin-left: 4px;
}

.sort-icon.inactive {
  color: #94a3b8;
}

@media (max-width: 1024px) {
  .filters-grid {
    grid-template-columns: repeat(2, minmax(180px, 1fr));
  }
}

@media (max-width: 640px) {
  .recipients-page {
    padding: 14px;
  }

  .page-header,
  .summary-row {
    align-items: stretch;
    flex-direction: column;
  }

  .filters-grid {
    grid-template-columns: 1fr;
  }

  .filter-actions {
    flex-direction: column;
  }

  .btn {
    width: 100%;
  }

  .pagination {
    flex-wrap: wrap;
  }
}
</style>